import { Immutable } from "immer";
import React from "react";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { ProductComparePrintOnlyBlock } from "tsi-common-react/src/components/ProductComparePrintOnlyBlock/index";

import { templates } from "@reactivated";
import type { RichTextBlock as RichTextBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * Block dispatcher map for the ``ProductComparePage`` body assembly. The
 * ``ProductComparePageBodyContent`` StreamBlock = ``ProductComparePageContent``
 * + ``Toolbox``, so its map is the full ``toolboxComponentsMap`` plus the three
 * extra ``ProductComparePageContent`` keys
 * (``server/cms/pages/product_compare_page.py`` ->
 * ``tsicommon.cms.blocks.product_compare.ProductComparePageContent``):
 *   - ``richtext``            -> Wagtail RichTextBlock (raw rich-text string,
 *                                same as the toolbox ``rich_text`` entry).
 *   - ``print_only_richtext`` -> ProductComparePrintOnlyBlock (takes value).
 *   - ``compare_grid``        -> ProductCompareGridSSR. Same ``ProductCompare
 *                                GridBlock`` as the toolbox ``product_compare_
 *                                grid`` key (different key name, same block /
 *                                same ``templates.ProductCompareGridBlock
 *                                Template`` SSR envelope), so it copies that
 *                                entry verbatim incl. ``showFirstAvailablePrice``
 *                                + ``isMinimal``.
 * All three are pure additions (the toolbox key is ``rich_text`` with an
 * underscore; there is no ``compare_grid`` / ``print_only_richtext`` in the
 * toolbox map), so the spread introduces no accidental overrides.
 */
const productComparePageComponentsMap = {
    ...toolboxComponentsMap,
    richtext: (block: { value: Immutable<RichTextBlockValue> }) => (
        <RichTextBlock value={block.value} />
    ),
    print_only_richtext: ProductComparePrintOnlyBlock,
    compare_grid: (block: {
        value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
    }) =>
        block.value ? (
            <ProductCompareGridSSR
                {...block.value}
                showFirstAvailablePrice
                isMinimal
            />
        ) : null,
} as const;

export const ProductComparePageBody = (
    props: Immutable<Pick<templates.StearnsProductComparePageTemplate, "page">>,
): React.JSX.Element => {
    // Reproduces the field-level
    // ``tsicommon_cms/blocks/product_compare_page.html`` chrome: the
    // ``<section class="product-compare-page-block">`` wrapper around the stream
    // (inherited via ``ProductComparePageContent.Meta.template``). The legacy
    // ``get_context`` vars are server-render-only; the SSR envelope recomputes
    // its own state, so only the section is load-bearing.
    return (
        <section className="product-compare-page-block">
            <StreamField
                tagName={React.Fragment}
                value={props.page.modules.value}
                components={productComparePageComponentsMap}
            />
        </section>
    );
};
